import { Controller } from "@hotwired/stimulus"
import { replacePlan } from "plan_storage"

// Plan-history — undo / redo for every plan edit. Listens for the
// `plan:change` event that every editor controller dispatches after a
// save, debounces rapid edits into a single snapshot, and exposes
// undo() / redo() actions that restore a previous JSON state.
//
// State model:
//   undoStack[]  — JSON strings, oldest → most recent at the top.
//                  The TOP entry IS the current plan, so we never
//                  pop ourselves; undo restores [top - 1].
//   redoStack[]  — JSON strings popped from the undo stack via undo().
//                  Cleared by any genuine new change.
//
// Apply path: writes the JSON to localStorage and broadcasts
// `plan:change` with detail.plan so every editor + preview controller
// re-hydrates. _suppress guards against recording our own broadcast
// back into the stack.
const STORAGE_KEY  = "pcp_plan_data"
const MAX_HISTORY  = 50
const COALESCE_MS  = 350

export default class extends Controller {
  static targets = ["undoBtn", "redoBtn"]

  connect() {
    this.undoStack = []
    this.redoStack = []
    this._suppress = false
    this._snapshot(this._currentJson())
    this._reflect()

    this._onChange = () => {
      if (this._suppress) return
      clearTimeout(this._coalesceTimer)
      this._coalesceTimer = setTimeout(() => this._snapshot(this._currentJson()), COALESCE_MS)
    }
    document.addEventListener("plan:change", this._onChange)

    // Ctrl+Z undo, Ctrl+Y or Ctrl+Shift+Z redo. Skip when the user is
    // typing in a normal input — let the browser's native field-level
    // undo handle that case.
    this._onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return
      const tag = (e.target?.tagName || "").toLowerCase()
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return
      const k = e.key.toLowerCase()
      if (k === "z" && !e.shiftKey) { e.preventDefault(); this.undo() }
      else if (k === "y" || (k === "z" && e.shiftKey)) { e.preventDefault(); this.redo() }
    }
    document.addEventListener("keydown", this._onKey)
  }

  disconnect() {
    document.removeEventListener("plan:change", this._onChange)
    document.removeEventListener("keydown", this._onKey)
    clearTimeout(this._coalesceTimer)
  }

  undo() {
    if (this.undoStack.length <= 1) {
      return
    }
    const current = this.undoStack.pop()
    this.redoStack.push(current)
    const previous = this.undoStack[this.undoStack.length - 1]
    this._apply(previous)
    this._reflect()
  }

  redo() {
    if (this.redoStack.length === 0) {
      return
    }
    const next = this.redoStack.pop()
    this.undoStack.push(next)
    this._apply(next)
    this._reflect()
  }

  _snapshot(json) {
    if (this.undoStack[this.undoStack.length - 1] === json) return
    this.undoStack.push(json)
    if (this.undoStack.length > MAX_HISTORY) this.undoStack.shift()
    this.redoStack = []
    this._reflect()
  }

  _apply(json) {
    this._suppress = true
    let plan = {}
    try { plan = JSON.parse(json) } catch (_) {}
    replacePlan(plan)
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true,
      detail: { path: "*", plan }
    }))
    // Release on next microtask so the snapshot recorder (which runs on
    // the same event loop turn via the coalesce timer) doesn't capture
    // our restored state as a fresh edit.
    queueMicrotask(() => {
      // Cancel any pending coalesce that might fire after release.
      clearTimeout(this._coalesceTimer)
      this._suppress = false
    })
  }

  _currentJson() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw || "{}"
    } catch (_) { return "{}" }
  }

  _reflect() {
    if (this.hasUndoBtnTarget) {
      this.undoBtnTarget.disabled = this.undoStack.length <= 1
    }
    if (this.hasRedoBtnTarget) {
      this.redoBtnTarget.disabled = this.redoStack.length === 0
    }
  }
}
